/**
 * Organisation app-branding colour maths for the CMS colour editor.
 *
 * Used by the Edit organisation dialog and the Add organisation wizard to warn
 * (never block — owner decision D5) when an organisation's colours will be hard
 * to read in the Athlyn employee app, and by the live phone preview to decide
 * whether text on a coloured fill is white or dark.
 *
 * The SAME maths, constants and test vectors live in employee-app
 * (`src/utils/brandPalette.ts`) — duplicated by rule, because platform repos
 * never share code. Change one ⇒ change both, and keep the shared vectors in
 * `src/__tests__/organizations/brandContrast.test.ts` identical.
 *
 * Mixing rule (both repos): each RGB channel = round(c + (target − c) × amount),
 * output upper-case `#RRGGBB`.
 */

export const WHITE = "#FFFFFF";
export const BLACK = "#000000";

/** Main colour is used for text/headings in the app ⇒ WCAG AA body text. */
export const MAIN_TEXT_MIN_CONTRAST = 4.5;
/**
 * Highlight is used for links/tags/labels and as a button fill. Below this the
 * CMS warns AND the app switches button text to dark — one threshold for both.
 */
export const HIGHLIGHT_TEXT_MIN_CONTRAST = 3.0;
/**
 * A brand colour drawn AS an icon, ring or text on a light surface must reach
 * this ratio against it, else `readableFg` swaps it for the organisation's
 * main colour (contract C-FG; same value as employee-app `FG_MIN_CONTRAST`).
 */
export const FG_MIN_CONTRAST = 3.0;
/** Below this ratio two brand colours read as "the same colour". */
export const SIMILAR_COLOUR_MAX_RATIO = 1.6;
/** …and only when their hues are this close (degrees). */
export const SIMILAR_HUE_MAX_DISTANCE = 30;
/** No accent set ⇒ accent = highlight mixed this far toward black (D2). */
export const ACCENT_DARKEN_AMOUNT = 0.28;
/** Tertiary (featured-card gradient end) = main mixed this far toward white. */
export const TERTIARY_LIGHTEN_AMOUNT = 0.22;

/** "Looks like a status colour" band (HSL). */
const STATUS_MIN_SATURATION = 0.45;
const STATUS_MIN_LIGHTNESS = 0.25;
const STATUS_MAX_LIGHTNESS = 0.65;
const ERROR_RED_HUE = 0;
const ERROR_RED_HUE_TOLERANCE = 14;
const SUCCESS_GREEN_HUE = 155;
const SUCCESS_GREEN_HUE_TOLERANCE = 18;

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

type Rgb = [number, number, number];

/** True for a `#RRGGBB` colour (the format the CMS API accepts). */
export function isValidHex(value: string | null | undefined): value is string {
  return typeof value === "string" && HEX_RE.test(value);
}

function toRgb(hex: string): Rgb {
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
}

function toHex([r, g, b]: Rgb): string {
  return `#${[r, g, b]
    .map((c) => Math.round(c).toString(16).padStart(2, "0"))
    .join("")
    .toUpperCase()}`;
}

function channelLuminance(c: number): number {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

/** WCAG 2.x relative luminance of a `#RRGGBB` colour. */
export function relativeLuminance(hex: string): number {
  const [r, g, b] = toRgb(hex);
  return (
    0.2126 * channelLuminance(r) +
    0.7152 * channelLuminance(g) +
    0.0722 * channelLuminance(b)
  );
}

/** WCAG 2.x contrast ratio between two `#RRGGBB` colours (1–21). */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/** Mix `hex` toward `target` by `amount` (0 = hex, 1 = target), per channel, rounded. */
export function mixHex(hex: string, target: string, amount: number): string {
  const from = toRgb(hex);
  const to = toRgb(target);
  return toHex([
    Math.round(from[0] + (to[0] - from[0]) * amount),
    Math.round(from[1] + (to[1] - from[1]) * amount),
    Math.round(from[2] + (to[2] - from[2]) * amount),
  ]);
}

/** Accent used when none is set: a darker shade of the highlight (D2). */
export function deriveAccent(highlight: string): string {
  return mixHex(highlight, BLACK, ACCENT_DARKEN_AMOUNT);
}

/** Tertiary brand colour: a lighter shade of the main colour. */
export function deriveTertiary(main: string): string {
  return mixHex(main, WHITE, TERTIARY_LIGHTEN_AMOUNT);
}

/** The accent the app will actually draw: the explicit one, else derived; null if neither is possible. */
export function effectiveAccent(
  accent: string | null | undefined,
  highlight: string | null | undefined,
): string | null {
  if (isValidHex(accent)) return accent;
  if (isValidHex(highlight)) return deriveAccent(highlight);
  return null;
}

/**
 * Text/icon colour to draw on `fill`. White is kept whenever it reaches
 * HIGHLIGHT_TEXT_MIN_CONTRAST (so the app only goes dark where the CMS has
 * warned); otherwise whichever of white / `ink` contrasts more.
 */
export function readableOn(fill: string, ink: string): string {
  if (!isValidHex(fill)) return WHITE;
  if (contrastRatio(fill, WHITE) >= HIGHLIGHT_TEXT_MIN_CONTRAST) return WHITE;
  if (!isValidHex(ink)) return WHITE;
  return contrastRatio(fill, ink) > contrastRatio(fill, WHITE) ? ink : WHITE;
}

/**
 * Colour to draw a brand colour AS an icon/text on `surface` (default white):
 * the colour itself when it reaches FG_MIN_CONTRAST there, else `ink` (the
 * organisation's main colour). Invalid colour or surface ⇒ `colour` unchanged.
 * Duplicated by rule with employee-app `readableFg` (`src/utils/brandPalette.ts`)
 * — change both together and keep the shared vectors identical.
 */
export function readableFg(colour: string, ink: string, surface: string = WHITE): string {
  if (!isValidHex(colour) || !isValidHex(surface)) return colour;
  return contrastRatio(colour, surface) >= FG_MIN_CONTRAST ? colour : ink;
}

/** HSL of a `#RRGGBB` colour: hue in degrees [0, 360), saturation/lightness 0–1. */
export function toHsl(hex: string): { h: number; s: number; l: number } {
  const [r, g, b] = toRgb(hex).map((c) => c / 255) as Rgb;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return { h: 0, s: 0, l };
  const s = d / (1 - Math.abs(2 * l - 1));
  let h: number;
  if (max === r) h = ((g - b) / d) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  h *= 60;
  if (h < 0) h += 360;
  return { h, s, l };
}

function hueDistance(a: number, b: number): number {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
}

function looksLikeStatus(hex: string, hue: number, tolerance: number): boolean {
  const { h, s, l } = toHsl(hex);
  return (
    s > STATUS_MIN_SATURATION &&
    l >= STATUS_MIN_LIGHTNESS &&
    l <= STATUS_MAX_LIGHTNESS &&
    hueDistance(h, hue) <= tolerance
  );
}

export type BrandWarningCode =
  | "MAIN_TOO_LIGHT"
  | "HIGHLIGHT_TEXT_WEAK"
  | "MAIN_HIGHLIGHT_TOO_SIMILAR"
  | "ACCENT_MAIN_TOO_SIMILAR"
  | "NEAR_ERROR_RED"
  | "NEAR_SUCCESS_GREEN"
  | "ACCENT_DERIVED"
  | "SWITCH_ON_COLOURS_MISSING";

/** `danger` = prominent destructive alert; `warning` = amber; `info` = neutral note. None block saving. */
export type BrandWarningSeverity = "danger" | "warning" | "info";

export type BrandColourRole = "main" | "highlight" | "accent";

export interface BrandWarning {
  code: BrandWarningCode;
  severity: BrandWarningSeverity;
  message: string;
  /** Set for per-colour warnings (status-colour look-alikes). */
  role?: BrandColourRole;
}

export interface BrandWarningsInput {
  main: string | null | undefined;
  highlight: string | null | undefined;
  accent: string | null | undefined;
  enabled: boolean;
}

const ROLE_LABEL: Record<BrandColourRole, string> = {
  main: "main",
  highlight: "highlight",
  accent: "accent",
};

/**
 * Typed, ordered warnings for an organisation's app colours. Empty, partial or
 * malformed colours are treated as "not set" — no maths is run on them.
 */
export function brandWarnings({
  main,
  highlight,
  accent,
  enabled,
}: BrandWarningsInput): BrandWarning[] {
  const warnings: BrandWarning[] = [];
  const m = isValidHex(main) ? main : null;
  const h = isValidHex(highlight) ? highlight : null;
  const a = isValidHex(accent) ? accent : null;

  if (m && contrastRatio(m, WHITE) < MAIN_TEXT_MIN_CONTRAST) {
    warnings.push({
      code: "MAIN_TOO_LIGHT",
      severity: "danger",
      message:
        "Text in the app will be hard to read with this main colour. Pick a darker colour or check with the client.",
    });
  }

  if (h && contrastRatio(h, WHITE) < HIGHLIGHT_TEXT_MIN_CONTRAST) {
    warnings.push({
      code: "HIGHLIGHT_TEXT_WEAK",
      severity: "warning",
      message:
        "This highlight colour is light: links, tags and labels in it will be hard to read on white, and the app will use dark text on highlight buttons.",
    });
  }

  if (
    m &&
    h &&
    contrastRatio(m, h) < SIMILAR_COLOUR_MAX_RATIO &&
    hueDistance(toHsl(m).h, toHsl(h).h) < SIMILAR_HUE_MAX_DISTANCE
  ) {
    warnings.push({
      code: "MAIN_HIGHLIGHT_TOO_SIMILAR",
      severity: "warning",
      message:
        "The main and highlight colours are very similar, so highlighted items will not stand out on the main-coloured header.",
    });
  }

  const effective = effectiveAccent(a, h);
  if (m && effective && contrastRatio(effective, m) < SIMILAR_COLOUR_MAX_RATIO) {
    warnings.push({
      code: "ACCENT_MAIN_TOO_SIMILAR",
      severity: "warning",
      message:
        "The accent is too close to the main colour: the notification dot and icon circles on the header will be hard to see.",
    });
  }

  const perColour: Array<[BrandColourRole, string | null]> = [
    ["main", m],
    ["highlight", h],
    ["accent", a],
  ];
  for (const [role, hex] of perColour) {
    if (hex && looksLikeStatus(hex, ERROR_RED_HUE, ERROR_RED_HUE_TOLERANCE)) {
      warnings.push({
        code: "NEAR_ERROR_RED",
        severity: "warning",
        role,
        message: `The ${ROLE_LABEL[role]} colour looks like the app's error red, so people may read it as a problem.`,
      });
    }
  }
  for (const [role, hex] of perColour) {
    if (hex && looksLikeStatus(hex, SUCCESS_GREEN_HUE, SUCCESS_GREEN_HUE_TOLERANCE)) {
      warnings.push({
        code: "NEAR_SUCCESS_GREEN",
        severity: "warning",
        role,
        message: `The ${ROLE_LABEL[role]} colour looks like the app's success green, so people may read it as a confirmation.`,
      });
    }
  }

  // Only when there is a valid highlight to darken — otherwise the note would promise a colour that does not exist.
  if (!a && h && (accent ?? "").trim() === "") {
    warnings.push({
      code: "ACCENT_DERIVED",
      severity: "info",
      message: "No accent set: the app uses a darker shade of the highlight.",
    });
  }

  if (enabled && (!m || !h)) {
    warnings.push({
      code: "SWITCH_ON_COLOURS_MISSING",
      severity: "info",
      message: "The app stays Athlyn until both colours are set.",
    });
  }

  return warnings;
}
